'use client';

// Adapted from T3 Code's RenderErrorBoundary.tsx at commit 0ae1091d019484d474fce0fe0a89195a82134041
// (MIT, Copyright 2026 T3 Tools Inc.). Full notice: public/third-party-licenses.txt.

import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

type RenderErrorBoundaryProps = {
  readonly children: ReactNode;
  readonly fallback: ReactNode;
  readonly resetKeys?: readonly unknown[];
  readonly onError?: (error: unknown, info: ErrorInfo) => void;
};

type RenderErrorBoundaryState = {
  readonly failed: boolean;
  readonly resetKeys?: readonly unknown[] | undefined;
};

export class RenderErrorBoundary extends Component<
  RenderErrorBoundaryProps,
  RenderErrorBoundaryState
> {
  override state = { failed: false, resetKeys: this.props.resetKeys };

  // Retry changed inputs without remounting healthy children or their controls.
  static getDerivedStateFromProps(
    { resetKeys }: RenderErrorBoundaryProps,
    state: RenderErrorBoundaryState,
  ) {
    if (
      resetKeys?.length !== state.resetKeys?.length ||
      resetKeys?.some((key, index) => !Object.is(key, state.resetKeys?.[index]))
    ) {
      return { failed: false, resetKeys };
    }
    return null;
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
